import { alpha, useTheme } from '@mui/material/styles'
import { useEffect, useState } from 'react'
import type {
  ChatConversation,
  TherapistInfo,
  PatientInfo,
} from '../services/chatApi'
import {
  listTherapistConversations,
  listPatientConversations,
  extractTherapistId,
  extractPatientId,
} from '../services/chatApi'
import useAuth from '@/hooks/useAuth'
import { UserRole } from '@/types'

interface ConversationListProps {
  onSelectConversation: (conversation: ChatConversation) => void
  selectedConversationSid?: string
}

const getParticipantName = (participant: unknown): string => {
  if (!participant || typeof participant === 'string') {
    // Covers missing / deleted users (e.g. patientId === null)
    return 'Deleted user'
  }

  const p = participant as {
    firstName?: string
    lastName?: string
    name?: string
  }

  const first = p.firstName ?? p.name ?? ''
  const last = p.lastName ?? ''
  const fullName = `${first} ${last}`.trim()

  return fullName || 'Deleted user'
}

const getParticipantAvatar = (participant: unknown): string | undefined => {
  if (!participant || typeof participant === 'string') return undefined

  const p = participant as {
    profilePhotoUrl?: string
    avatarUrl?: string
    avatar?: string
  }

  return p.profilePhotoUrl || p.avatarUrl || p.avatar
}

export const ConversationList = ({
  onSelectConversation,
  selectedConversationSid,
}: ConversationListProps) => {
  const theme = useTheme()
  const { user } = useAuth()
  const [conversations, setConversations] = useState<ChatConversation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const isTherapist = user?.role === UserRole.THERAPIST
  const userId = user?.id

  useEffect(() => {
    if (!userId) {
      setLoading(false)
      return
    }

    const fetchConversations = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = isTherapist
          ? await listTherapistConversations(userId)
          : await listPatientConversations(userId)
        setConversations(data)
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load conversations'
        )
      } finally {
        setLoading(false)
      }
    }

    fetchConversations()
  }, [userId, isTherapist])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div
          className="w-8 h-8 border-4 rounded-full animate-spin"
          style={{
            borderColor: alpha(theme.palette.primary.main, 0.2),
            borderTopColor: theme.palette.primary.main,
          }}
        />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-4 text-sm text-center text-slate-500">
        <p>{error}</p>
      </div>
    )
  }

  if (conversations.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-4 text-sm text-center text-slate-500">
        <p>No conversations yet</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full pb-4 overflow-y-auto">
      {conversations.map(conversation => {
        // For therapist view: show patient info (patientId may be object)
        // For patient view: show therapist info (therapistId may be object)
        const participantInfo = isTherapist
          ? conversation.patientId
          : conversation.therapistId

        const participantName = getParticipantName(participantInfo)
        const participantAvatar = getParticipantAvatar(participantInfo)

        // Use conversationId as primary, fallback to conversationSid
        const conversationId =
          conversation.conversationId ?? conversation.conversationSid ?? ''
        const isSelected = conversationId === selectedConversationSid
        const unreadCount = isTherapist
          ? (conversation.therapistUnread ?? 0)
          : (conversation.patientUnread ?? 0)

        return (
          <button
            key={conversationId}
            onClick={() => onSelectConversation(conversation)}
            className={`flex w-full items-center gap-3 border-b border-slate-100 p-4 text-left transition hover:bg-slate-50 ${
              isSelected ? 'bg-indigo-50' : ''
            }`}
            style={
              isSelected
                ? { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
                : undefined
            }
          >
            {participantAvatar ? (
              <img
                src={participantAvatar}
                alt={participantName}
                className="object-cover w-12 h-12 rounded-full shadow"
              />
            ) : (
              <div className="flex items-center justify-center w-12 h-12 text-sm font-semibold rounded-full shadow bg-slate-200 text-slate-600">
                {participantName && participantName.trim() !== ''
                  ? participantName
                      .split(' ')
                      .filter(Boolean)
                      .map(n => n[0] ?? '')
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)
                  : 'DU'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate text-slate-800">
                {participantName}
              </p>
              <p className="text-sm truncate text-slate-500">
                {conversation.lastMessage || 'No messages yet'}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              {conversation.lastMessageAt && (
                <p className="text-xs text-slate-400">
                  {new Date(conversation.lastMessageAt).toLocaleDateString(
                    'en-US',
                    {
                      month: 'short',
                      day: 'numeric',
                    }
                  )}
                </p>
              )}
              {unreadCount > 0 && (
                <span
                  className="flex items-center justify-center w-5 h-5 text-xs font-semibold text-white rounded-full"
                  style={{ backgroundColor: theme.palette.primary.main }}
                >
                  {unreadCount}
                </span>
              )}
            </div>
          </button>
        )
      })}
    </div>
  )
}
